import { useState, useEffect, useCallback } from "react"
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { Trans, useTranslation } from "react-i18next"
import { useRoleUsers, useAssignRoleUsers } from "../hooks/useRoles"
import type { RoleUser } from "../models/role"
import { getErrorMessage } from "../utils"
import { STATUS_MAP } from "../../admin-users/models/adminUser"

interface Props {
  roleName: string
}

export default function RoleMembersTabContent({ roleName }: Props) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const pageSize = 20

  const { data, isLoading } = useRoleUsers(roleName, { page, page_size: pageSize })
  const assignUsersMutation = useAssignRoleUsers()

  const [addUserOpened, setAddUserOpened] = useState(false)

  const handleRemoveUser = (user: RoleUser) => {
    modals.openConfirmModal({
      title: t("adminRoles.confirmRemoveUser"),
      children: (
        <Text size="sm">
          {t("adminRoles.confirmRemoveUserMessage", { username: user.username })}
        </Text>
      ),
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        assignUsersMutation.mutate(
          { name: roleName, add: [], remove: [user.id] },
          {
            onSuccess: () => {
              notifications.show({
                color: "green",
                title: t("common.success"),
                message: t("adminRoles.removeUserSuccess"),
              })
            },
            onError: (err) => {
              notifications.show({
                color: "red",
                title: t("common.error"),
                message: getErrorMessage(err),
              })
            },
          },
        )
      },
    })
  }

  const users = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <Stack>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {t("adminRoles.memberCount", { count: total })}
        </Text>
        <Button
          size="xs"
          variant="light"
          onClick={() => setAddUserOpened(true)}
          leftSection={<i className="fa fa-plus" />}
        >
          <Trans i18nKey="adminRoles.addUser" />
        </Button>
      </Group>

      {users.length === 0 && !isLoading ? (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          <Stack align="center" gap="xs">
            <Text size="32px" c="gray.3">
              <i className="fa fa-users" />
            </Text>
            {t("adminRoles.noMembers")}
          </Stack>
        </Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("adminRoles.username")}</Table.Th>
              <Table.Th>{t("adminRoles.name")}</Table.Th>
              <Table.Th>{t("adminRoles.email")}</Table.Th>
              <Table.Th>{t("adminRoles.agency")}</Table.Th>
              <Table.Th>{t("adminRoles.status")}</Table.Th>
              <Table.Th style={{ width: 60 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {users.map((u) => {
              const statusInfo = STATUS_MAP[u.status] ?? STATUS_MAP[0]
              return (
                <Table.Tr key={u.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{u.username}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{u.full_name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{u.email}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{u.agency_name ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={statusInfo.color} size="sm" variant="light">
                      {t(`adminRoles.${statusInfo.label}`)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label={t("adminRoles.removeUser")}>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => handleRemoveUser(u)}
                      >
                        <i className="fa fa-times" />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="sm">
          <Button
            variant="default"
            size="xs"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <Trans i18nKey="common.back" />
          </Button>
          <Text size="sm" c="dimmed">
            {t("adminRoles.pageOf", { page, total: totalPages })}
          </Text>
          <Button
            variant="default"
            size="xs"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <Trans i18nKey="common.next" />
          </Button>
        </Group>
      )}

      <AddUserModal
        opened={addUserOpened}
        onClose={() => setAddUserOpened(false)}
        roleName={roleName}
      />
    </Stack>
  )
}

interface AddUserModalProps {
  opened: boolean
  onClose: () => void
  roleName: string
}

function AddUserModal({ opened, onClose, roleName }: AddUserModalProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const assignUsersMutation = useAssignRoleUsers()

  const { data: searchResults } = useRoleUsers(roleName, { search, page_size: 50 })
  const allUsers = searchResults?.items ?? []

  const candidates = allUsers

  const toggleUser = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleAdd = () => {
    if (selectedIds.size === 0) return
    assignUsersMutation.mutate(
      { name: roleName, add: Array.from(selectedIds), remove: [] },
      {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: t("common.success"),
            message: t("adminRoles.addUserSuccess"),
          })
          setSelectedIds(new Set())
          setSearch("")
          onClose()
        },
        onError: (err) => {
          notifications.show({
            color: "red",
            title: t("common.error"),
            message: getErrorMessage(err),
          })
        },
      },
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("adminRoles.addUserTitle")}
      size="lg"
      centered
    >
      <Stack>
        <TextInput
          placeholder={t("adminRoles.searchUsersPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftSection={<i className="fa fa-search" style={{ fontSize: 14 }} />}
        />
        <div style={{ maxHeight: 300, overflowY: "auto" }}>
          {candidates.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="md">
              {t("adminRoles.noUsersFound")}
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 40 }} />
                  <Table.Th>{t("adminRoles.username")}</Table.Th>
                  <Table.Th>{t("adminRoles.name")}</Table.Th>
                  <Table.Th>{t("adminRoles.email")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {candidates.map((u) => (
                  <Table.Tr
                    key={u.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleUser(u.id)}
                  >
                    <Table.Td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(u.id)}
                        readOnly
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{u.username}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{u.full_name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{u.email}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </div>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            <Trans i18nKey="common.cancel" />
          </Button>
          <Button
            onClick={handleAdd}
            loading={assignUsersMutation.isPending}
            disabled={selectedIds.size === 0}
          >
            <Trans i18nKey="adminRoles.addSelected" count={selectedIds.size} />
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
